'use client';

import { ComponentContainer } from '@/components/atoms/ComponentContainer/ComponentContainer';
import { Image } from '@/components/atoms/Image/Image';
import { TProject, TProjectVisibility } from '@/types/global';
import { Typography } from '@/components/atoms/Typography/Typography';
import { CustomLink } from '@/components/atoms/CustomLink/CustomLink';
import { useTranslations } from 'next-intl';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { useEffect, useRef, useState } from 'react'; // ⬅️ added useState
import { usePageStore } from '@/store/usePageStore';
import * as Styled from './RecentProjectsGrid.styled';
import { emailEncoded } from '@/utils/constants';
import { VimeoVideo } from '@/components/atoms/VimeoVideoCustomControls/VimeoVideo';
import { usePathname } from 'next/dist/client/components/navigation';


export interface IRecentProjectsGrid {
  projects: TProject[];
}

gsap.registerPlugin(ScrollTrigger);

export const RecentProjectsGrid: React.FC<IRecentProjectsGrid> = ({
  projects,
}) => {
  const heightWindowUnit = usePageStore((state) => state.heightWindowUnit);
  const isDobleColumn = (imgOrder: number) => [3, 8].includes(imgOrder + 1);
  const isSmootherReady = usePageStore((s) => s.isSmootherReady);
  const refProjectsGrid = useRef<HTMLDivElement>(null);
  const refTopContent = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const tPrivate = useTranslations('work');
  const contentPrivate = tPrivate.raw('filteredProjectsGallery') as {
    titlePrivateProject: string;
    textPrivateProject: string;
  };
  const refObserverWithOverflow = useRef<HTMLDivElement>(null);

  // 🔹 New: control when to render Vimeo videos
  const [shouldRenderVideos, setShouldRenderVideos] = useState(false);

  const t = useTranslations('home');
  const texts = t.raw('recentProjects') as {
    title: string;
    viewMore: string;
    slugViewMore: string;
  };

  useEffect(() => {
    // Reset executed animation when leaving the homepage
    usePageStore.getState().setProjectBreadcrumbPath(pathname);
  }, [pathname]);

  // 🔹 New: enable videos after intersection OR 5s after page load (whichever comes first)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const node = refObserverWithOverflow.current;

    // Fallback timer: enable after 5 seconds
    timer = setTimeout(() => setShouldRenderVideos(true), 5000);

    // IntersectionObserver: enable when ObserverWithOverflow enters the viewport
    const observer =
      typeof window !== 'undefined'
        ? new IntersectionObserver(
            (entries) => {
              for (const entry of entries) {
                if (entry.isIntersecting) {
                  setShouldRenderVideos(true);
                }
              }
            },
            { root: null, rootMargin: '0px', threshold: 0.1 }
          )
        : null;

    if (observer && node) observer.observe(node);

    return () => {
      if (timer) clearTimeout(timer);
      if (observer) observer.disconnect();
    };
  }, []);

  useGSAP(() => {
    if (typeof window === 'undefined') return;
    if (!isSmootherReady || !refProjectsGrid.current || !refTopContent.current) return;
  
    const el = refProjectsGrid.current;
    const elTopContent = refTopContent.current;

    const isAnimationExecuted = usePageStore.getState().executedAnimations.recentProjectsGrid;

    if (isAnimationExecuted) {
      gsap.set([el, elTopContent], { opacity: 1, y: 0 });
      return;
    }
  
    const ctx = gsap.context(() => {
      // Set initial values
      gsap.set([elTopContent, el], { opacity: 0 });
      gsap.set(el, { y: heightWindowUnit * 5 });
  
      gsap.to(elTopContent, {
        opacity: 1,
        duration: 1.2,
        ease: 'power4.out',
        scrollTrigger: {
          trigger: el,
          start: `top+=${heightWindowUnit * 1} bottom`,
          toggleActions: 'play none none none',
        },
      });
  
      gsap.to(el, {
        opacity: 1,
        y: 0,
        duration: 2,
        ease: 'power4.out',
        scrollTrigger: {
          trigger: el,
          start: `top+=${heightWindowUnit * 5} bottom`,
          toggleActions: 'play none none none',
        },
        onComplete: () => {
          const executedAnimations = usePageStore.getState().executedAnimations;
          usePageStore.getState().setExecutedAnimations({
            ...executedAnimations,
            recentProjectsGrid: true,
          });
        }
      });
    }, [refProjectsGrid, refTopContent]);
  
    return () => ctx.revert(); // This cleanly kills only what's inside this context
  }, [isSmootherReady]);

  const isUserLogged = usePageStore((s) => s.isUserLogged);

  const isBlockedByPrivacy = (visibility: TProjectVisibility = 'open') => {
    return (visibility === 'private' && !isUserLogged);
  };

  return (
    <ComponentContainer >
      <Styled.TopContent ref={refTopContent}>
        <Typography
          text={texts.title}
          size="lg"
          as="h2"
          fontFamily="helvetica-regular"
        />
        <CustomLink
          text={texts.viewMore}
          href={texts.slugViewMore}
          color="secondary"
          hoverColor="primary"
        />
      </Styled.TopContent>
      <Styled.ObserverWithOverflow ref={refObserverWithOverflow}/>
      <Styled.ProjectsGrid ref={refProjectsGrid}>
        {projects &&
          Array.isArray(projects) &&
          projects.map((project, i) => (
            <CustomLink
              key={`project-${i}`}
              href={project.slug}
              imageHoverEffect={true}
              saveScrollPosition={true}
              isDisabled={isBlockedByPrivacy(project.visibility)}
              className={isBlockedByPrivacy(project.visibility) ? 'private-project' : ''}
            >
              <Styled.Project
                key={`project-${i}`}
                isDoubleColumn={isDobleColumn(i)}
              >
                {project.video && project.video.vimeoId && shouldRenderVideos ? (
                  <VimeoVideo
                    aspectRatio={isDobleColumn(i) ? 'auto' : '1'}
                    includeContainer={true}
                    title={`video ${project.title}`}
                    type={project.video.type}
                    vimeoId={project.video.vimeoId}
                    vimeoMetadata={project.video.vimeoMetadata || undefined}
                    includesSound={false}
                    includesControls={false}
                    vimeoSizeLimit={4}
                  />
                ) : (
                  project.image && (
                    <Image
                      {...project.image}
                      aspectRatio={isDobleColumn(i) ? 'auto' : '1'}
                      includeContainer={true}
                      alt={project.image.alt}
                      lazy={i > 7 ? true : false}
                    />
                  )
                )}
                {isBlockedByPrivacy(project.visibility) && (
                  <Styled.TextExclusiveContent
                    aspectRatio="4/3"
                    data-is-logged={isUserLogged}
                  >
                    <Typography
                      text={contentPrivate.titlePrivateProject}
                      color="white"
                      size="base"
                    />
                    <Typography
                      text={{
                        en: `To view the full details of this project, click on this project to contact us at ${emailEncoded}`,
                        es: `Para ver los detalles completos de este proyecto, haga clic en este proyecto para contáctenos en ${emailEncoded}`
                      }}
                      color="white"
                      size="sm"
                    />
                  </Styled.TextExclusiveContent>
                )}
                <Styled.TextContainer>
                  <Typography text={project.title} />
                  <Typography text={project.location} color="secondary" />
                </Styled.TextContainer>
              </Styled.Project>
            </CustomLink>
          ))}
      </Styled.ProjectsGrid>
    </ComponentContainer>
  );
};
